import { useEffect } from 'react';
import { Modal } from '../Modal';
import type { PendingChanges } from '../../stores/resourceViewerStore';

function message({ redaction, notes }: PendingChanges): string {
  if (redaction && notes) return 'You have both pending redaction(s) and unsaved note(s).';
  if (redaction) return 'You have pending redaction(s).';
  return 'You have unsaved note(s).';
}

/** Port of partials/controls/pending-redactions-notes.html. */
export function PendingChangesModal({ pendingChanges, onDisregard, onCancel, targetDocument }: {
  pendingChanges: PendingChanges;
  onDisregard: () => void;
  onCancel: () => void;
  targetDocument?: Document;
}) {
  // Esc cancels. Capture phase + stopPropagation keeps the viewer's hotkeys from seeing it.
  useEffect(() => {
    const doc = targetDocument ?? document;
    function onKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    }
    doc.addEventListener('keydown', onKeydown, true);
    return () => {
      try { doc.removeEventListener('keydown', onKeydown, true); } catch { /* noop */ }
    };
  }, [onCancel, targetDocument]);

  return (
    <Modal
      title="Pending Changes Detected"
      icon="fas fa-warning"
      onClose={onCancel}
      targetDocument={targetDocument}
      testId="viewer-pending-changes-modal"
      footer={
        <>
          <button type="button" className="btn btn-primary" onClick={onDisregard} data-testid="viewer-pending-changes-disregard-btn">Disregard Changes</button>
          <button type="button" className="btn btn-default" onClick={onCancel} data-testid="viewer-pending-changes-cancel-btn">Cancel</button>
        </>
      }
    >
      {message(pendingChanges)}
    </Modal>
  );
}
